import { useMemo, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { MoreActionsMenu } from '../components/ui/MoreActionsMenu'
import { Breadcrumbs } from '../components/ui/Breadcrumbs'
import { SectionTabPanel, SectionTabs, useSectionTab } from '../components/ui/SectionTabs'
import { displayCode, displayDomainValue, governanceTierLabel, stateLabel } from '../lib/display-labels'
import { ErrorAlert, PageHeader } from '../portfolio/PortfolioCommon'
import {
  getApprovalCoordination,
  getApprovalTask,
  recallApprovalRequirement,
  reassignApprovalRequirement,
  recordApprovalDecision,
  type ApprovalConditionInput,
} from './approval-api'
import { DecisionBriefChecklist, DecisionBriefFacts, DecisionBriefSection } from './DecisionBrief'
import { ApprovalAssigneePicker } from './ContextualPersonAssignment'

function object(value: unknown): Record<string, unknown> { return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {} }
function objects(value: unknown): Record<string, unknown>[] { return Array.isArray(value) ? value.map(object) : [] }
function text(value: unknown): string { return typeof value === 'string' ? value : '' }
function bool(value: unknown): boolean { return value === true }
function titleCase(value: string) { return displayDomainValue(value) }

const emptyCondition = (): ApprovalConditionInput => ({ title: '', description: '', completionCriteria: '' })
const approvalTabs = [
  { id: 'review' as const, label: '1. Review' },
  { id: 'supporting' as const, label: '2. Supporting information' },
  { id: 'decision' as const, label: '3. Your decision' },
]

export function ApprovalTaskPage() {
  const { requirementId = '' } = useParams()
  const queryClient = useQueryClient()
  const [decision, setDecision] = useState<'approved' | 'approved_with_conditions' | 'rejected' | ''>('')
  const [rationale, setRationale] = useState('')
  const [validFrom, setValidFrom] = useState('')
  const [validTo, setValidTo] = useState('')
  const [conditions, setConditions] = useState<ApprovalConditionInput[]>([emptyCondition()])
  const [assigneeId, setAssigneeId] = useState('')
  const [transferReason, setTransferReason] = useState('')
  const [recallReason, setRecallReason] = useState('')
  const [activeCoordinationAction, setActiveCoordinationAction] = useState<'assign' | 'recall' | null>(null)
  const [activeTab, setActiveTab] = useSectionTab(approvalTabs, decision || rationale ? 'decision' : 'review')

  const taskQuery = useQuery({
    queryKey: ['r1-approval-task', requirementId],
    queryFn: async () => object(await getApprovalTask(requirementId)),
    enabled: Boolean(requirementId),
  })
  const coordinationQuery = useQuery({
    queryKey: ['r1-approval-coordination', requirementId],
    queryFn: async () => object(await getApprovalCoordination(requirementId)),
    enabled: Boolean(requirementId),
  })
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['r1-approval-task', requirementId] }),
      queryClient.invalidateQueries({ queryKey: ['r1-approval-coordination', requirementId] }),
      queryClient.invalidateQueries({ queryKey: ['r1-my-actions'] }),
    ])
  }
  const decisionMutation = useMutation({
    mutationFn: () => recordApprovalDecision(
      requirementId,
      decision as 'approved' | 'approved_with_conditions' | 'rejected',
      rationale,
      validFrom ? new Date(validFrom).toISOString() : null,
      validTo ? new Date(validTo).toISOString() : null,
      decision === 'approved_with_conditions' ? conditions : [],
    ),
    onSuccess: () => void refresh(),
  })
  const coordination = coordinationQuery.data ?? {}
  const eligibleApprovers = objects(coordination.eligibleApprovers)
  const reassignMutation = useMutation({
    mutationFn: () => reassignApprovalRequirement(
      requirementId,
      Number(coordination.assignmentRevision) || 0,
      assigneeId,
      transferReason,
    ),
    onSuccess: () => {
      setAssigneeId('')
      setTransferReason('')
      void refresh()
    },
  })
  const recallMutation = useMutation({
    mutationFn: () => recallApprovalRequirement(
      requirementId,
      Number(coordination.assignmentRevision) || 0,
      recallReason,
    ),
    onSuccess: () => {
      setRecallReason('')
      void refresh()
    },
  })

  const task = taskQuery.data ?? {}
  const useCase = object(task.aiUseCase)
  const request = object(task.approvalRequest)
  const requirement = object(task.requirement)
  const outcome = object(task.outcome)
  const owners = object(task.owners)
  const risks = objects(task.risks)
  const riskPositions = objects(task.riskPosition)
  const approvalReadiness = object(task.approvalReadiness)
  const readinessBlockers = objects(approvalReadiness.blockers)
  const controls = objects(task.controls)
  const confirmations = objects(task.specialistConfirmations)
  const outstandingConfirmations = confirmations.filter((confirmation) => !['completed', 'superseded', 'cancelled'].includes(text(confirmation.status)))
  const priorDecisions = objects(task.decisions)
  const ownerChoices = useMemo(() => [object(owners.business), object(owners.technical)].filter((owner) => text(owner.membershipId)), [owners])
  const canDecide = text(requirement.status) === 'ready'
    && text(request.status) === 'pending'
    && readinessBlockers.length === 0
    && outstandingConfirmations.length === 0
    && text(coordination.currentMembershipId) === text(coordination.assignedMembershipId)
  const rationaleRequired = decision === 'approved_with_conditions' || decision === 'rejected'
  const error = taskQuery.error ?? coordinationQuery.error ?? decisionMutation.error ?? reassignMutation.error ?? recallMutation.error
  const coordinationMenuActions = [
    bool(coordination.canReassignApproval) ? { id: 'assign', label: text(coordination.assignmentState) === 'unassigned' ? 'Assign approver' : 'Reassign approver', onSelect: () => setActiveCoordinationAction('assign' as const) } : null,
    bool(coordination.canRecallApproval) ? { id: 'recall', label: 'Recall assignment', danger: true, onSelect: () => setActiveCoordinationAction('recall' as const) } : null,
  ].filter((item): item is NonNullable<typeof item> => Boolean(item))

  const updateCondition = (index: number, patch: Partial<ApprovalConditionInput>) => {
    setConditions((current) => current.map((condition, currentIndex) => currentIndex === index ? { ...condition, ...patch } : condition))
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    decisionMutation.mutate()
  }

  if (taskQuery.isLoading) return <div className="page-stack decision-brief-page"><section className="panel"><p>Loading approval brief…</p></section></div>
  if (!taskQuery.data) return <div className="page-stack"><ErrorAlert error={error ?? new Error('Approval task is not available')} /></div>

  return (
    <div className="page-stack">
      <Breadcrumbs items={[{ label: 'AI Uses', to: '/ai-register' }, { label: text(useCase.title), to: `/ai-register/${text(useCase.id)}` }, { label: 'Approval' }]} />
      <PageHeader
        eyebrow="Executive approval"
        title={`Decision required — ${text(useCase.title)}`}
        description="Review the purpose, risks, controls and specialist reviews before recording your decision."
      />
      <ErrorAlert error={error} />

      <section className="panel approval-task-summary" aria-label="Approval status and next action">
        <div>
          <p className="eyebrow">Approval status</p>
          <p><Link className="decision-brief-title-link" to={`/ai-register/${text(useCase.id)}`}>{text(useCase.title)}</Link></p>
          <h2>{outstandingConfirmations.length > 0 ? 'Waiting for specialist review' : readinessBlockers.length === 0 ? 'Ready for decision' : `${readinessBlockers.length} outstanding ${readinessBlockers.length === 1 ? 'blocker' : 'blockers'}`}</h2>
          <p className="muted">{outstandingConfirmations.length > 0
            ? `${outstandingConfirmations.length} required specialist ${outstandingConfirmations.length === 1 ? 'confirmation is' : 'confirmations are'} still outstanding.`
            : readinessBlockers.length === 0
              ? 'All required specialist confirmations and governance prerequisites are complete.'
              : 'Complete the outstanding governance prerequisites before a decision is recorded.'}</p>
          <DecisionBriefFacts facts={[
            { label: 'Governance tier', value: governanceTierLabel(request.governanceTier) },
            { label: 'Approval stage', value: stateLabel(text(request.requestedStage)) },
            { label: 'Operational status', value: stateLabel(text(useCase.operationalState)) },
            { label: 'Your role', value: displayCode(text(requirement.authorityType) || 'business_approver') },
          ]} />
        </div>
        {canDecide ? <button className="button primary" type="button" onClick={() => setActiveTab('decision')}>Record final decision</button> : null}
      </section>

      {(text(request.status) === 'draft' || text(request.status) === 'changes_requested') ? <section className="panel" aria-labelledby="approval-assignment-heading">
        <h2 id="approval-assignment-heading">Assign this approval</h2>
        <p>Choose the named person for this requirement, or invite them from here. Swell preserves the deterministic approval type, sequencing and separation-of-duties checks.</p>
        <ApprovalAssigneePicker requirementId={requirementId} onChanged={async () => { await refresh() }} />
      </section> : null}

      {bool(coordination.canReassignApproval) || bool(coordination.canRecallApproval) ? <section className="confirmation-coordination approval-coordination" aria-labelledby="approval-coordination-heading">
        <div className="approval-coordination-menu"><h2 id="approval-coordination-heading" className="eyebrow">Approval assignment</h2><MoreActionsMenu label="More approval actions" actions={coordinationMenuActions} /></div>
        {bool(coordination.canReassignApproval) ? <details open={activeCoordinationAction === 'assign'} hidden={activeCoordinationAction !== 'assign'}><summary className="button secondary compact">{text(coordination.assignmentState) === 'unassigned' ? 'Assign approver' : 'Reassign approver'}</summary><div className="form-stack">
          <p className="muted">Assignment changes keep the approval type, sequence, frozen request basis and separation-of-duties checks unchanged.</p>
          <label>New person<select value={assigneeId} onChange={(event) => setAssigneeId(event.target.value)}>
            <option value="">Choose a compatible organisation member...</option>
            {eligibleApprovers.filter((member) => text(member.membershipId) !== text(coordination.assignedMembershipId)).map((member) => <option key={text(member.membershipId)} value={text(member.membershipId)}>{text(member.displayName)}</option>)}
          </select></label>
          <label>Reason<textarea rows={3} required value={transferReason} onChange={(event) => setTransferReason(event.target.value)} /></label>
          <button className="button secondary" type="button" disabled={!assigneeId || !transferReason.trim() || reassignMutation.isPending} onClick={() => reassignMutation.mutate()}>{reassignMutation.isPending ? 'Saving...' : text(coordination.assignmentState) === 'unassigned' ? 'Assign approver' : 'Reassign approver'}</button>
        </div></details> : null}
        {bool(coordination.canRecallApproval) ? <details className="approval-recall-controls" open={activeCoordinationAction === 'recall'} hidden={activeCoordinationAction !== 'recall'}><summary className="button text compact">Recall assignment</summary><div className="form-stack">
          <p className="muted">Recall removes the current approver but keeps this deterministic Approval Requirement outstanding. It does not approve, reject, waive or delete it.</p>
          <label>Reason<textarea rows={3} required value={recallReason} onChange={(event) => setRecallReason(event.target.value)} /></label>
          <button className="button text" type="button" disabled={!recallReason.trim() || recallMutation.isPending} onClick={() => recallMutation.mutate()}>{recallMutation.isPending ? 'Recalling...' : 'Recall assignment'}</button>
        </div></details> : null}
      </section> : null}

      <SectionTabs tabs={approvalTabs} activeTab={activeTab} onSelect={setActiveTab} label="Approval sections" idPrefix="approval" />
      <SectionTabPanel idPrefix="approval" tab="review" activeTab={activeTab}>
      <DecisionBriefSection
        icon="document"
        eyebrow="What you are approving"
        title={text(request.requestedScope) || 'Use of this AI capability'}
        description={text(useCase.businessPurpose)}
      >
        {text(useCase.expectedOutcome) ? <p className="decision-brief-callout"><strong>Expected outcome:</strong> {text(useCase.expectedOutcome)}</p> : null}
      </DecisionBriefSection>

      <DecisionBriefSection
        icon="shield"
        eyebrow="Your role in this decision"
        title="Why your approval is required"
        description="You are the business approver for this AI use. Your role is to decide whether it should proceed after considering its purpose, risks, controls and specialist reviews."
        tone="attention"
      >
        <DecisionBriefFacts facts={[{ label: 'Approval authority', value: 'Business approver' }]} />
      </DecisionBriefSection>

      <DecisionBriefSection
        icon="info"
        eyebrow="Decision guide"
        title="What to review before deciding"
        description="This brief separates risk acceptance from AI use approval. Check the current risk position, any open governance blockers, the required controls and specialist determinations before recording your decision."
      >
        <DecisionBriefChecklist items={[
          { label: readinessBlockers.length ? String(readinessBlockers.length) + ' governance blocker' + (readinessBlockers.length === 1 ? '' : 's') + ' still needs attention.' : 'No recorded governance blockers are preventing this approval.', status: readinessBlockers.length ? 'attention' : 'ready' },
          { label: outstandingConfirmations.length
            ? String(outstandingConfirmations.length) + ' required specialist confirmation' + (outstandingConfirmations.length === 1 ? ' is' : 's are') + ' still outstanding.'
            : confirmations.length
              ? 'All required specialist confirmations are complete.'
              : 'No specialist confirmations were required for this assessment.', status: outstandingConfirmations.length ? 'attention' : 'ready' },
          { label: controls.length ? String(controls.length) + ' control' + (controls.length === 1 ? '' : 's') + ' supports this approval.' : 'No additional controls were identified as required by this assessment.', status: controls.length ? 'neutral' : 'ready' },
        ]} />
      </DecisionBriefSection>
      </SectionTabPanel>

      <SectionTabPanel idPrefix="approval" tab="supporting" activeTab={activeTab}>

      <section className="panel approval-supporting-summary" aria-labelledby="approval-purpose-heading">
        <h2 id="approval-purpose-heading">Purpose</h2>
        <p>{text(useCase.businessPurpose) || text(request.requestedScope) || 'No purpose has been recorded.'}</p>
        {text(useCase.expectedOutcome) ? <p><strong>Expected outcome:</strong> {text(useCase.expectedOutcome)}</p> : null}
        {text(useCase.informationInvolved) ? <><h3>Information involved</h3><p>{text(useCase.informationInvolved)}</p></> : null}
      </section>

      <section className="panel" aria-labelledby="risk-heading">
        <h2 id="risk-heading">What could go wrong?</h2>
        <p><strong>Current risk position.</strong> Risk acceptance is handled separately. Any risks requiring formal acceptance must be resolved before this AI use can be approved.</p>
        {riskPositions.length > 0 ? <div className="form-stack">
          {riskPositions.map((risk) => {
            const inherent = object(risk.inherentRating)
            const residual = object(risk.residualRating)
            const acceptance = object(risk.acceptance)
            return <article key={text(risk.riskId)}>
              <div className="section-heading compact">
                <div><strong>{text(risk.riskReference)} — {text(risk.title)}</strong><p>{text(risk.riskStatement)}</p></div>
                <Link className="text-link" to={`/ai-risks/${text(risk.riskId)}`}>View Risk</Link>
              </div>
              <p className="muted">{displayDomainValue(risk.categoryCode)} · Inherent {titleCase(text(inherent.effectiveBand) || 'unrated')} · Residual {titleCase(text(residual.effectiveBand) || 'unrated')} · Appetite {titleCase(text(residual.appetitePosition) || 'not set')} · Treatment {titleCase(text(risk.treatmentStatus) || 'not started')}</p>
              {bool(residual.stale) ? <p className="inline-alert neutral">Residual rating requires recalculation because its recognised Control or evidence basis changed.</p> : null}
              {text(acceptance.decision) ? <p><strong>Risk Acceptance:</strong> {titleCase(text(acceptance.decision))}</p> : null}
            </article>
          })}
        </div> : risks.length === 0 ? <p className="muted">No risks requiring acceptance were identified for this approval.</p> : <div className="form-stack">
          {risks.map((risk) => <article key={text(risk.id)}>
            <strong>{text(risk.title)}</strong>
            <p>{text(risk.description)}</p>
            <p className="muted">{displayDomainValue(risk.dimension)} · Residual judgement: {displayDomainValue(risk.residualJudgement, 'Not recorded')} · {displayDomainValue(risk.status)}</p>
          </article>)}
        </div>}
      </section>

      {readinessBlockers.length > 0 ? <section className="panel" aria-labelledby="approval-readiness-heading">
        <h2 id="approval-readiness-heading">Approval readiness</h2>
        <p>This request still has governance blockers. Swell will not treat Risk Acceptance as AI Use Approval.</p>
        <div className="form-stack">{readinessBlockers.map((blocker, index) => <article key={`${text(blocker.code)}-${index}`}><strong>{titleCase(text(blocker.code))}</strong><p>{text(blocker.message)}</p>{text(blocker.riskReference) ? <p className="muted">{text(blocker.riskReference)}</p> : null}</article>)}</div>
      </section> : null}

      {controls.length > 0 ? <section className="panel" aria-labelledby="controls-heading">
        <h2 id="controls-heading">Controls that support this approval</h2>
        <div className="form-stack">
          {controls.map((control) => <article key={text(control.id)}>
            <strong>{text(control.controlCode)} — {text(control.title)}</strong>
            <p>{text(control.description)}</p>
            <p className="muted">Stage {displayDomainValue(control.requiredStage)} · Status {displayDomainValue(control.status)}{bool(control.verificationRequired) ? ` · Verification ${displayDomainValue(control.verificationOutcome, 'Pending')}` : ''}</p>
          </article>)}
        </div>
      </section> : null}

      {confirmations.length > 0 ? <section className="panel" aria-labelledby="specialist-heading">
        <h2 id="specialist-heading">Specialist confirmations</h2>
        <div className="form-stack">
          {confirmations.map((confirmation) => <article key={text(confirmation.requirementId)}>
            <strong>{displayDomainValue(confirmation.type)} — {displayDomainValue(text(confirmation.decision) || text(confirmation.status))}</strong>
            <p>{text(confirmation.reason)}</p>
            {text(confirmation.comments) ? <p>{text(confirmation.comments)}</p> : null}
            {text(confirmation.conditions) ? <p><strong>Specialist conditions:</strong> {text(confirmation.conditions)}</p> : null}
          </article>)}
        </div>
      </section> : null}
      </SectionTabPanel>

      <SectionTabPanel idPrefix="approval" tab="decision" activeTab={activeTab}>

      {priorDecisions.length > 0 ? <section className="panel">
        <h2>Recorded decision</h2>
        {priorDecisions.map((item) => <article key={text(item.id)}>
          <strong>{displayDomainValue(item.decision)}</strong>
          <p>{text(item.rationale)}</p>
          <p className="muted">Recorded {text(item.decidedAt) ? new Date(text(item.decidedAt)).toLocaleString() : ''}</p>
        </article>)}
      </section> : null}

      {canDecide ? <section className="panel" aria-labelledby="decision-heading">
        <h2 id="decision-heading">Record your decision</h2>
        <p>Once recorded, your decision cannot be changed. If you approve with conditions, Swell will track those conditions until they are completed.</p>
        <section className="decision-summary-card executive-decision-summary" aria-labelledby="executive-summary-heading">
          <h3 id="executive-summary-heading">Decision summary</h3>
          <dl>
            <div><dt>AI use</dt><dd>{text(useCase.title)}</dd></div>
            <div><dt>Purpose</dt><dd>{text(useCase.businessPurpose) || text(request.requestedScope) || 'Not recorded'}</dd></div>
            <div><dt>Governance tier</dt><dd>{governanceTierLabel(request.governanceTier)}</dd></div>
            <div><dt>Operational status</dt><dd>{stateLabel(text(useCase.operationalState))}</dd></div>
            <div><dt>Unresolved risks</dt><dd>{riskPositions.length || risks.length ? `${riskPositions.length || risks.length} recorded — review supporting information` : 'None'}</dd></div>
            <div><dt>Required controls</dt><dd>{controls.length || 'None identified'}</dd></div>
            <div><dt>Specialist reviews</dt><dd>{confirmations.length || 'None required'}</dd></div>
            <div><dt>Outstanding blockers</dt><dd>{readinessBlockers.length || 'None'}</dd></div>
          </dl>
        </section>
        <form className="form-stack decision-brief-form" onSubmit={submit}>
          <fieldset className="decision-card-options" disabled={decisionMutation.isPending}>
            <legend>Decision</legend>
            <label><input type="radio" name="approval-decision" checked={decision === 'approved'} onChange={() => setDecision('approved')} /><span><strong>Approve</strong><small>The AI use may proceed as proposed.</small></span></label>
            <label><input type="radio" name="approval-decision" checked={decision === 'approved_with_conditions'} onChange={() => setDecision('approved_with_conditions')} /><span><strong>Approve with conditions</strong><small>The AI use may proceed provided the conditions below are completed.</small></span></label>
            <label><input type="radio" name="approval-decision" checked={decision === 'rejected'} onChange={() => setDecision('rejected')} /><span><strong>Do not approve</strong><small>The AI use should not proceed in its current form.</small></span></label>
          </fieldset>
          {decision === 'approved' ? <label>Comments <span className="muted">(optional)</span>
            <textarea rows={4} value={rationale} onChange={(event) => setRationale(event.target.value)} />
            <small>Add any context that should be retained with your approval.</small>
          </label> : null}
          {decision === 'approved_with_conditions' ? <label>Why are conditions required?
            <textarea rows={4} value={rationale} onChange={(event) => setRationale(event.target.value)} required />
          </label> : null}
          {decision === 'rejected' ? <label>Why should this AI use not proceed?
            <textarea rows={4} value={rationale} onChange={(event) => setRationale(event.target.value)} required />
            <small>Explain what would need to change before this use could be reconsidered.</small>
          </label> : null}
          <details className="approval-period-disclosure">
            <summary>Set an approval period (optional)</summary>
            <p>Use this if the approval should only apply between specific dates.</p>
            <div className="form-grid two-column">
              <label>Valid from <span className="muted">(optional)</span>
                <input type="datetime-local" value={validFrom} onChange={(event) => setValidFrom(event.target.value)} />
              </label>
              <label>Valid until <span className="muted">(optional)</span>
                <input type="datetime-local" value={validTo} onChange={(event) => setValidTo(event.target.value)} />
              </label>
            </div>
          </details>

          {decision === 'approved_with_conditions' ? <div className="form-stack">
            <h3>Conditions to be completed</h3>
            {conditions.map((condition, index) => <fieldset key={index}>
              <legend>Condition {index + 1}</legend>
              <label>Title<input value={condition.title} onChange={(event) => updateCondition(index, { title: event.target.value })} required /></label>
              <label>Description<textarea rows={3} value={condition.description} onChange={(event) => updateCondition(index, { description: event.target.value })} required /></label>
              <label>Completion criteria<textarea rows={3} value={condition.completionCriteria} onChange={(event) => updateCondition(index, { completionCriteria: event.target.value })} required /></label>
              <label>Owner
                <select value={condition.ownerMembershipId ?? ''} onChange={(event) => updateCondition(index, { ownerMembershipId: event.target.value || undefined })}>
                  <option value="">Not assigned</option>
                  {ownerChoices.map((owner) => <option key={text(owner.membershipId)} value={text(owner.membershipId)}>{text(owner.displayName)} — {text(owner.relationship)}</option>)}
                </select>
              </label>
              <label>Due date <span className="muted">(optional)</span><input type="date" value={condition.dueDate ?? ''} onChange={(event) => updateCondition(index, { dueDate: event.target.value || undefined })} /></label>
              {conditions.length > 1 ? <button className="button secondary" type="button" onClick={() => setConditions((current) => current.filter((_, currentIndex) => currentIndex !== index))}>Remove condition</button> : null}
            </fieldset>)}
            <button className="button secondary" type="button" onClick={() => setConditions((current) => [...current, emptyCondition()])}>Add another condition</button>
          </div> : null}

          <button className="button primary" type="submit" disabled={!decision || (rationaleRequired && !rationale.trim()) || decisionMutation.isPending}>{decisionMutation.isPending ? 'Recording decision…' : 'Record final decision'}</button>
        </form>
      </section> : <section className="panel"><h2>Approval status</h2><p>This approval requirement is {displayDomainValue(requirement.status)}. Overall request outcome: {displayDomainValue(outcome.outcome, 'Pending')}.</p></section>}
      </SectionTabPanel>
    </div>
  )
}
